<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import { DocumentTextIcon, PrinterIcon, RefreshIcon } from '@heroicons/vue/outline'
import { ref, watch } from 'vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import FolderPlusIcon from '../../../base/icons/FolderPlusIcon.vue'
import { useLegalAccountDataState } from '../../../../utilities/account/useAccountDataLegal'
import AccountPanelLegalMissingDataAlert from '../AccountPanelLegalMissingDataAlert.vue'
import { refreshLegalProfileSignatures } from '../../../../utilities/api'
import SignSignatureDocumentButton from '../../personal/signatures/SignSignatureDocumentButton.vue'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import BaseButtonPrimary from '../../../base/BaseButtonPrimary.vue'
import ManualSignatureUpload from '../signatures/ManualSignatureUpload.vue'
import { computedAsync } from '@vueuse/core'
import { isEsacLegalComplianceProfileComplete } from '../../../wizard/ESAC/form/forms'
import AccountPanelLegalMissingComplianceProfileDataAlert from '../AccountPanelLegalMissingComplianceProfileDataAlert.vue'
import { getAccountLink } from '../../../../utilities/useLinks'

const state = useLegalAccountDataState()

const refreshingSignatures = ref(false)

async function refreshSignatures() {
    refreshingSignatures.value = true
    const response = await refreshLegalProfileSignatures(
        state.legalProfile.legal_profile_uid,
        encodeURI(getAccountLink())
    )
    state.legalProfile.signatures = response.data
    refreshingSignatures.value = false
}

const isComplianceProfileComplete = computedAsync(async () => {
    if (getPlatformSettings().compliance_profile === 'esac') {
        return await isEsacLegalComplianceProfileComplete()
    }
    return true
}, false)

watch(isComplianceProfileComplete, () => {
    if (
        isComplianceProfileComplete.value === true &&
        getPlatformSettings().compliance_profile === 'esac' &&
        state.legalProfile.signatures.length === 0
    ) {
        refreshSignatures()
    }
})

function openDocumentLink(url) {
    window.open(url, '_blank').focus()
}

function openLink(link) {
    window.open(link, '_blank').focus()
}
</script>

<template>
    <div>
        <AccountPanelLegalMissingDataAlert
            v-if="!state.hasAllRequiredProfileAttributesForInvestment"
            inline
            :description-text="$t('signatures.complete_data_description_for_signatures')"
            class="my-9" />
        <AccountPanelLegalMissingComplianceProfileDataAlert
            v-else-if="!isComplianceProfileComplete"
            inline
            :description-text="$t('signatures.complete_compliance_profile')"
            class="my-9" />
        <div v-else>
            <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:px-6">
                <div
                    v-if="state.legalProfile.signatures.length > 0"
                    class="overflow-hidden border border-gray-200 bg-white shadow sm:col-span-1 sm:rounded-md">
                    <ul role="list" class="ml-0 list-none divide-y divide-gray-200">
                        <li v-for="signature in state.legalProfile.signatures" :key="signature.link" class="mb-0">
                            <div>
                                <div class="flex items-center px-2 py-3 sm:px-4">
                                    <div class="min-w-0 flex-1 items-center md:flex">
                                        <div class="flex-shrink-0">
                                            <div
                                                class="relative flex items-center justify-center rounded-full bg-white p-1">
                                                <div class="relative">
                                                    <DocumentTextIcon
                                                        class="h-12 w-12 text-gray-300"></DocumentTextIcon>
                                                    <ExclamationCircleIcon
                                                        v-if="signature.signed_at === null"
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                                                    <CheckCircleIcon
                                                        v-else
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500"></CheckCircleIcon>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="min-w-0 flex-1 px-4">
                                            <div>
                                                <p class="truncate text-sm font-semibold text-gray-900">
                                                    {{ signature.document_type }}
                                                </p>
                                                <p class="mt-1 flex items-center text-sm text-gray-500">
                                                    Status:
                                                    {{
                                                        signature.signed_at
                                                            ? $t('signatures.signed')
                                                            : $t('signatures.not_signed')
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="block pt-6 md:pt-0">
                                            <div class="flex flex-col space-y-2">
                                                <SignSignatureDocumentButton
                                                    v-if="signature.link && state.legalProfile.is_owner"
                                                    class="px-8 font-bold"
                                                    :signature="signature">
                                                </SignSignatureDocumentButton>
                                                <BaseButtonMuted
                                                    :icon="PrinterIcon"
                                                    @click="openDocumentLink(signature.unsigned_document_link)"
                                                    >{{ $t('signatures.print') }}</BaseButtonMuted
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else class="pb-6 text-center">
                    <FolderPlusIcon class="mx-auto h-12 w-12 text-gray-400" />
                    <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('signatures.no_signatures') }}</h3>
                    <p class="mt-1 text-sm text-gray-500">{{ $t('signatures.no_signatures_description') }}</p>
                </div>
                <div class="items-center justify-between space-y-2 md:flex md:space-y-0">
                    <p class="max-w-md pt-1 text-xs text-gray-400">
                        {{ $t('signatures.regenerate_signatures_notice') }}
                    </p>
                    <BaseButtonMuted
                        :loading="refreshingSignatures"
                        :icon="RefreshIcon"
                        size="lg"
                        @click="refreshSignatures()"
                        >{{ $t('signatures.regenerate_signatures') }}</BaseButtonMuted
                    >
                </div>
            </div>
            <div class="grid grid-cols-1 gap-x-4 gap-y-4 px-4 py-5 sm:px-6">
                <p class="text-lg font-bold text-black">Beneficial Owner Signatures</p>
                <div
                    v-if="state.legalProfile.beneficial_owners.length > 0"
                    class="overflow-hidden border border-gray-200 bg-white shadow sm:col-span-1 sm:rounded-md">
                    <ul role="list" class="ml-0 list-none divide-y divide-gray-200">
                        <li
                            v-for="beneficialOwner in state.legalProfile.beneficial_owners"
                            :key="beneficialOwner.email"
                            class="mb-0">
                            <div v-for="signature in beneficialOwner.signatures" :key="signature.id">
                                <div class="flex items-center px-2 py-3 sm:px-4">
                                    <div class="min-w-0 flex-1 items-center md:flex">
                                        <div class="flex-shrink-0">
                                            <div
                                                class="relative flex items-center justify-center rounded-full bg-white p-1">
                                                <div class="relative">
                                                    <DocumentTextIcon
                                                        class="h-12 w-12 text-gray-300"></DocumentTextIcon>
                                                    <ExclamationCircleIcon
                                                        v-if="signature.signed_at === null"
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                                                    <CheckCircleIcon
                                                        v-else
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500"></CheckCircleIcon>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="min-w-0 flex-1 px-4">
                                            <div>
                                                <p class="truncate text-sm font-semibold text-gray-900">
                                                    {{ signature.document_type }} for {{ beneficialOwner.first_name }}
                                                    {{ beneficialOwner.last_name }}
                                                </p>
                                                <p class="mt-1 flex items-center text-sm text-gray-500">
                                                    Status:
                                                    {{
                                                        signature.signed_at
                                                            ? $t('signatures.signed')
                                                            : $t('signatures.not_signed')
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="block pt-6 md:pt-0">
                                            <div class="flex flex-col space-y-2">
                                                <BaseButtonPrimary
                                                    v-if="signature.signed_at && signature.signed_document_link"
                                                    @click="openLink(signature.signed_document_link)"
                                                    >{{ $t('signatures.show_document') }}</BaseButtonPrimary
                                                >
                                                <BaseButtonMuted
                                                    :icon="PrinterIcon"
                                                    @click="openDocumentLink(signature.unsigned_document_link)"
                                                    >{{ $t('signatures.print') }}</BaseButtonMuted
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="px-4 pb-2">
                                    <ManualSignatureUpload
                                        :signature="signature"
                                        name="signature_document"
                                        class="my-2"
                                        max-file-size="5MB"
                                        label=""
                                        @update:modelValue="state.fetchAccountData()" />
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else class="pb-6 text-center">
                    <FolderPlusIcon class="mx-auto h-12 w-12 text-gray-400" />
                    <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('signatures.no_signatures') }}</h3>
                    <p class="mt-1 text-sm text-gray-500">{{ $t('signatures.no_signatures_description') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
